/*
 * Copyright (c) 2024 Roboto Technologies, Inc.
 */

import { List } from "@mui/material";
import * as React from "react";

import { AuthZCategoryListItem } from "@/shared/components/AuthZCategoryListItem";
import { useAPICall } from "@/shared/services/apiHooks";
import { AuthZTuple, GetAccessResponse, RobotoEndpoint } from "@/types";

const datasetsAccessEndpoint: RobotoEndpoint = (pathParams) => {
  const datasetId = pathParams?.datasetId;

  return `/datasets/${datasetId}/access`;
};

interface DatasetPermissionsProps {
  datasetId: string;
}

export const DatasetPermissions: React.FC<DatasetPermissionsProps> = ({
  datasetId,
}) => {
  const { data, loading, refetch } = useAPICall<GetAccessResponse>({
    endpoint: datasetsAccessEndpoint,
    method: "GET",
    pathParams: { datasetId: datasetId },
  });

  React.useEffect(() => {
    refetch({
      pathParams: { datasetId: datasetId },
    });
  }, [datasetId, refetch]);

  const relationsToTuples: Map<string, AuthZTuple[]> = new Map();

  if (data) {
    data.data.relations.forEach((relation) => {
      const tuples = relationsToTuples.get(relation.relation) || [];
      tuples.push(relation);
      relationsToTuples.set(relation.relation, tuples);
    });
  }

  const editAccessEndpointPathParams = { datasetId: datasetId };

  return (
    <div>
      <List sx={{ width: "100%", padding: 0 }}>
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Owner"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"owner"}
          relations={relationsToTuples.get("owner") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Groups"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"associated_group"}
          relations={relationsToTuples.get("associated_group") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Collections"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"parent"}
          relations={relationsToTuples.get("parent") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Viewer"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"viewer"}
          relations={relationsToTuples.get("viewer") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Editor"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"editor"}
          relations={relationsToTuples.get("editor") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
      </List>
    </div>
  );
};
